import { cn } from "@/lib/utils";
import { Skeleton } from "@components/ui/skeleton";
import { Spinner } from "@components/ui/spinner";
import React from "react";

type Props = { repeat?: number; className?: string };

const SkeletonOne: React.FC<Props> = ({ repeat = 1, className = "" }) => {
  return (
    <>
      <div className={cn(["relative w-full skeleton-before", className])}>
        {Array.from({ length: repeat }).map((_, i: number) => (
          <div key={i} className={`flex items-center space-x-[40px] space-y-2`}>
            <div className="flex space-x-1">
              {Array.from({ length: 3 }).map((_, index: number) => (
                <Skeleton key={index} className="h-[5px] w-[20px]" />
              ))}
            </div>
            <div className="">
              <Skeleton className="h-[90px] w-[91px]" />
            </div>
            <div className="flex flex-col space-y-[10px]">
              <div className="flex space-x-1">
                {Array.from({ length: 2 }).map((_, index: number) => (
                  <Skeleton
                    key={index}
                    className={`h-[5px] ${
                      index % 2 === 0 ? "w-[35px]" : "w-[50px]"
                    }`}
                  />
                ))}
              </div>
              <div className="flex space-x-1">
                {Array.from({ length: 1 }).map((_, index: number) => (
                  <Skeleton key={index} className="h-[5px] w-[45px]" />
                ))}
              </div>
            </div>
            <div className="flex flex-col space-y-[10px]">
              <div className="flex space-x-1">
                {Array.from({ length: 10 }).map((_, index: number) => (
                  <Skeleton
                    key={index}
                    className={`h-[5px] ${
                      index % 2 === 0 ? "w-[35px]" : "w-[50px]"
                    }`}
                  />
                ))}
              </div>
              <div className="flex space-x-1">
                {Array.from({ length: 10 }).map((_, index: number) => (
                  <Skeleton
                    key={index}
                    className={`h-[5px] ${
                      index % 2 != 0 ? "w-[35px]" : "w-[50px]"
                    }`}
                  />
                ))}
              </div>
            </div>
            <div className="flex space-x-3">
              {Array.from({ length: 2 }).map((_, index: number) => (
                <Skeleton key={index} className="h-[45px] w-[45px]" />
              ))}
            </div>
          </div>
        ))}
        <div className="absolute  text-[#1DA077] top-1/2 left-1/2 -translate-x-1/2 h-[40px] w-[40px]">
          <Spinner />
        </div>
      </div>
    </>
  );
};

const SkeletonTwo: React.FC<Props> = () => {
  return (
    <div className="min-h-[60vh] space-y-4">
      <Skeleton className="h-[40px] " />
      <div className="flex gap-4">
        <Skeleton className="h-[40px] w-full" />
        <Skeleton className="h-[40px] w-full" />
      </div>
      <Skeleton className="h-[40px] " />
      <div className="flex gap-4">
        <Skeleton className="h-[40px] w-full" />
        <Skeleton className="h-[40px] w-full" />
      </div>
      <Skeleton className="h-[140px] w-full" />
      <Skeleton className="h-[50px] w-full mt-[40px]" />
    </div>
  );
};
function TableSkeleton() {
  return (
    <div className="w-full px-4  ">
      {/* Table Header */}
      <div className="bg-[#e8f6f1] border mt-[27px] border-green-200  p-4 ">
        <div className="grid grid-cols-6 gap-4 h-[55px] items-center">
          <Skeleton className="h-4 w-6 bg-[#b9e2d5]" />
          <Skeleton className="h-4 w-24 bg-[#b9e2d5]" />
          <Skeleton className="h-4 w-16 bg-[#b9e2d5]" />
          <Skeleton className="h-4 w-12 bg-[#b9e2d5]" />
          <Skeleton className="h-4 w-16 bg-[#b9e2d5]" />
          <div></div>
        </div>
      </div>

      {/* Table Rows */}
      <div className="border border-t-0 border-green-200 rounded-b-lg bg-white">
        {Array.from({ length: 6 }, (_, index) => (
          <div
            key={index}
            className="grid grid-cols-6 gap-4 items-center p-4 border-b border-gray-100 last:border-b-0"
          >
            {/* Checkbox */}
            <div className="flex items-center">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-4 w-2 ml-2" />
            </div>

            {/* Featured Image */}
            <div>
              <Skeleton className="h-16 w-24 rounded-lg" />
            </div>

            {/* Section Badge */}
            <div>
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>

            {/* Title */}
            <div>
              <Skeleton className="h-4 w-28" />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Skeleton className="h-6 w-8 rounded" />
              <Skeleton className="h-6 w-12 rounded" />
            </div>

            {/* Empty column for spacing */}
            <div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GridCardSkeleton() {
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 mt-[25px] lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-md border overflow-hidden"
          >
            {/* Title Section */}
            <div className="p-4 space-y-2 flex justify-between">
              <Skeleton className="h-5 w-[40px]" />
              <Skeleton className="h-5 w-[70px]" />
            </div>

            {/* Large Image/Content Section */}
            <div className="relative bg-gray-100 mx-4 rounded-lg overflow-hidden">
              <Skeleton className="h-[312px] w-full" />

              {/* Navigation arrows placeholders */}
              <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>

              {/* Text overlay placeholder */}
              <div className="absolute top-4 left-4">
                <Skeleton className="h-6 w-20" />
              </div>
            </div>

            {/* Action Buttons Section */}
            <div className="flex justify-between p-4 bg-gray-50">
              <Skeleton className="h-8 w-full rounded" />
              <Skeleton className="h-8 w-full rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title Section */}
          <div className="space-y-2">
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-12 w-full rounded-md" />
          </div>

          {/* Team Member and Student Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-12 w-full rounded-md" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-12 w-full rounded-md" />
            </div>
          </div>

          {/* Description Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-5 w-20" />
            </div>
          </div>

          {/* Question Section */}
          <div className="space-y-2">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-24 w-full rounded-md" />
          </div>

          {/* Answer Section */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-14" />

            {/* Rich Text Editor Toolbar */}

            {/* Editor Content Area */}
            <Skeleton className="h-48 w-full rounded-b-md" />
          </div>
        </div>

        {/* Right Column - Upload Areas */}
        <div className="space-y-8">
          {/* Feature Image Upload */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-24" />
            <div className="border-2 border-dashed rounded-lg p-8 text-center space-y-3">
              <Skeleton className="h-12 w-12 rounded-full mx-auto" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-48 mx-auto" />
                <Skeleton className="h-4 w-40 mx-auto" />
                <Skeleton className="h-4 w-44 mx-auto" />
              </div>
            </div>
          </div>

          {/* Feature Video Upload */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-24" />
            <div className="border-2 border-dashed rounded-lg p-8 text-center space-y-3">
              <Skeleton className="h-12 w-12 rounded-full mx-auto" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-48 mx-auto" />
                <Skeleton className="h-4 w-44 mx-auto" />
                <Skeleton className="h-4 w-44 mx-auto" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
function ResponseSkeleton() {
  return (
    <div className=" p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <Skeleton className="h-7 w-32" />
        <Skeleton className="h-4 w-64" />
      </div>

      {/* Form Fields */}
      <div className="space-y-6">
        {/* Full Name */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <div className="flex">
            <Skeleton className="h-10 w-16 rounded-l-md" />
            <Skeleton className="h-10 flex-1 rounded-r-md" />
          </div>
        </div>

        {/* Subject */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-14" />
          <div className="flex items-center space-x-2">
            <Skeleton className="h-3 w-3 rounded-full" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>

        {/* Address */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        {/* Message */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-32 w-full rounded-md" />
        </div>
      </div>
    </div>
  );
}

export {
  SkeletonOne,
  SkeletonTwo,
  TableSkeleton,
  GridCardSkeleton,
  ResponseSkeleton,
  FormSkeleton,
};
